import React, {useEffect, useRef, useState} from "react";
import clsx from "clsx";
import {Button, Label, Radio} from "flowbite-react";
import {RenderableTitle, SurveySectionsRenderingModel} from "data/models/survey/base";
import WebFont from "webfontloader";
import {useRecommendedFonts} from "data/access/survey-question-pages/fonts";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown} from "@fortawesome/free-solid-svg-icons";
import {traitName} from "data/models/shared/bc-sheet/brand-trait";
import LoadingSurvey from "components/Survey/LoadingSurvey";
import {assertNoError} from "helpers/asserts";
import {FontDefinition} from "data/models/shared/recommended-fonts";

export type FontSelect_SurveyQuestionPage_Props = {
  visible: boolean;
  title: RenderableTitle;
  questionId: string;
  surveySectionsRenderingModel: SurveySectionsRenderingModel | null,
  value: FontDefinition | undefined;
  onChange: (id: string, value: FontDefinition) => void;
}

export default function FontSelectSurveyQuestionPage(
    {
      visible,
      title,
      questionId,
      surveySectionsRenderingModel,
      value,
      onChange
    }: FontSelect_SurveyQuestionPage_Props
) {
  const [recommendedFonts, recommendedFontsLoading, recommendedFontsError] = useRecommendedFonts();
  const [openedFontsGroup, setOpenedFontsGroup] = useState(1);
  const showMoreRef = useRef<null | HTMLButtonElement>(null)

  const fontChoices = recommendedFonts?.fonts;

  useEffect(() => {
    if (!visible || fontChoices === undefined) {
      return;
    }
    WebFont.load({
      google: {
        families: fontChoices.map(({fonts}) => fonts).flat().map(font => font.family),
      },
    });
  }, [visible, fontChoices]); // Empty dependency array ensures it only loads on mount

  useEffect(() => {
    // Scroll into "Show More" button when "openedFontsGroup" changes.
    if (openedFontsGroup > 1 && showMoreRef !== null && showMoreRef.current !== null) {
      showMoreRef.current.scrollIntoView({behavior: "smooth"})
    }
  }, [openedFontsGroup, showMoreRef]);

  function fontCase(fontName: string, text: string) {
    return (fontName.toUpperCase() === fontName) ? text.toUpperCase() : text;
  }

  assertNoError(recommendedFontsError);
  if (recommendedFontsLoading) {
    return visible && <LoadingSurvey /> || null;
  }

  function handleShowMore() {
    if (openedFontsGroup < (fontChoices?.length ?? 0)) {
      setOpenedFontsGroup(openedFontsGroup + 1);
    }
  }

  return <div className={"flex flex-col w-full"}>
    <div className={clsx(QUESTION_CONTAINER_CLASS, "mb-24")}>
      <div className={"my-4 text-center"}>
        <span className={"text-3xl font-bold"}>{title}</span>
      </div>
      <div className={"text-center"}>
        <p className={"my-8 font-normal text-xl text-neutral-500"}>
          Based  on what you told us about {surveySectionsRenderingModel?.businessName ?? ''},
          we recommend these fonts for your brand.
          Your brand font will be used for your business name and to make your brand recognizable and memorable.
          Make sure your business name is readable in this font and you like the way it looks.
        </p>
      </div>
      <div className={"mb-8 text-center"}>
        <p>
          <span className={"text-black text-xl font-bold"}>Select one brand font</span>
        </p>
      </div>
      <div>
        <fieldset name={questionId} className="flex flex-col gap-4 justify-center" autoFocus={true}>
          {fontChoices?.slice(0, openedFontsGroup).map(({traitKey, fonts}, index) => <div key={index}
                                                                                          className={"flex flex-col justify-center mb-12"}>
            {index > 0 && <div className={"mb-4 text-center"}>
              <span className={"text-neutral-500 font-semibold"}>These fonts are more {traitName(traitKey)}</span>
            </div>}
            {fonts.map((font, index) => <div key={index} className="flex flex-col items-center gap-2 text-center">
              <Radio
                  className="hidden peer"
                  id={`${questionId}.${font.family}`}
                  value={font.family}
                  checked={value?.family === font.family}
                  onChange={() => onChange(questionId, font)}
              />
              <Label htmlFor={`${questionId}.${font.family}`}
                     className={"w-full inline-flex p-5 my-1 text-black bg-white border-2 border-neutral-50 rounded-2xl cursor-pointer peer-checked:border-bc-blue hover:text-neutral-600 hover:border-neutral-200"}>
                <div className={"w-full"}>
                <span className={"text-5xl"} style={{
                  fontFamily: font.name,
                  fontWeight: font.weight ?? 400,
                  fontStyle: font.italic ? "italic" : "normal"
                }}>{fontCase(font.name, surveySectionsRenderingModel?.businessName ?? "Business Name Missing")}</span>
                </div>
              </Label>
            </div>)}
          </div>)}
          {openedFontsGroup < (fontChoices?.length ?? 0) && <div className={"mx-auto"}>
              <Button
                  className={"px-12 rounded-lg"}
                  color={"gray"}
                  onClick={() => handleShowMore()}
                  ref={showMoreRef}
              >
                  <div className={"flex flex-col"}>
                      <div><span className={"text-neutral-500 font-bold"}>Show more</span></div>
                      <div><FontAwesomeIcon icon={faChevronDown} size={"2xl"}/></div>
                  </div>
              </Button>
          </div>}
        </fieldset>
      </div>
    </div>
  </div>
}
